/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stepper, Step, StepLabel, StepIconProps, StepConnector, Grid2, IconButton } from '@mui/material';
import { styled } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { grey } from '@mui/material/colors';
import styles from './CycleOverViewList.module.css';
import Stimulation from './Stimulation/Stimulation';
import OocyteRetrieval from './OocyteRetrieval/OocyteRetrieval';
import SemenProcessing from './SemenProcessing/SemenProcessing';
import Fertilization from './Fertilization/Fertilization';
import EmbryoCulture from './EmbryoCulture/EmbryoCulture';
import EmbryoTransferOutcome from './EmbryoTransferOutcome/EmbryoTransferOutcome';
import Grid from '@mui/material/Grid';
import { CycleDetailsType, getcycleList, steps, updateDates, getcycleOverviewReport } from './CycleDetailsHelper';
import { PROCEDURES_WITHOUT_LMP } from './CreateNewCycleHelper';
import { Avatar, Tooltip } from '@mui/material';
import AvatarGroup from '@mui/material/AvatarGroup';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { usePatientContext } from '../SubPatinetProvider';
import { useCycle } from '../cycleContext/CycleContext';
import HPIBack from '../../assets/SVG/HPIBack.svg';
import SemenFreezingReport from './Semen Freezing/SemenFreezingReport';
import { convertUnderscoreToTitle } from '../../utils/commonUtils';
import EditIcon from '@mui/icons-material/EditOutlined';
import { ButtonState, getButtonStates } from '../../utils/permissionService';
import { getmedicationprotocol, MedicationProtocolResponse } from './CreateNewCycleHelper';
import { useClinic } from '../ClinicProvider';
import { ReactComponent as PrinterIcon } from '../../assets/SVG/printer (1).svg';
import ReusableReportComponent from '../printReports/ReusableReportComponent';
import CycleOverviewReport from './CycleOverviewReport/CycleOverviewReport';
import IUIDetails from './IUI/IUIDetails';
import { formatDate } from '../../utils/dateUtils';


const CustomConnector = styled(StepConnector)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderTop: '2px solid #e0e0e0',
  '& .MuiStepConnector-line': {
    transition: 'all 0.3s ease',
  },
  '&.Mui-active .MuiStepConnector-line': {
    borderColor: '#0C7DED',
  },
  '&.Mui-completed .MuiStepConnector-line': {
    borderColor: '#4caf50',
  },
}));

function CustomStepIcon(props: StepIconProps) {
  const { active, completed } = props;

  return (
    <div
      style={{
        backgroundColor: completed ? '#4caf50' : active ? '#4caf50' : grey[500],
        color: '#fff',
        borderRadius: '50%',
        width: 24,
        height: 24,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        transition: 'all 0.3s ease',
      }}
    >
      {completed ? <CheckCircleIcon /> : <span>{props.icon}</span>}
    </div>
  );
}



interface CycleDetailsProps {
  handleCycleListClick: () => void;
  cycle: string;
  gender?: 'MALE' | 'FEMALE';
}

const CycleDetails: React.FC<CycleDetailsProps> = ({ handleCycleListClick, cycle,gender }) => {
  const { t } = useTranslation('cycleOverview_config');
  const { t: tGeneral } = useTranslation('general');
  const [activeStep, setActiveStep] = useState(0);
  const [stepsArray, setSteps] = useState<steps[]>([]);
  const [cycleDetails, setCycleDetails] = useState<CycleDetailsType | null>(null);
  // determine if current procedure should disable LMP editing
  const isLmpDisabled = cycleDetails
    ? PROCEDURES_WITHOUT_LMP.includes(cycleDetails.procedure?.procedures || '')
    : false;
  // 'start', 'end', 'lmp', or null
  const [editingDateField, setEditingDateField] = useState<null | 'start' | 'end' | 'lmp'>(null);
  const [editedStartDate, setEditedStartDate] = useState<string | undefined>(undefined);
  const [editedEndDate, setEditedEndDate] = useState<string | undefined>(undefined);
  const [editedLmpDate, setEditedLmpDate] = useState<string | undefined>(undefined);
  const { ClinicId } = useClinic();

  // Helper to update dates in backend
  type CycleDatesPayload = Partial<Pick<CycleDetailsType, 'cycle_start_date' | 'cycle_end_date' | 'lmp_date' | 'medication_protocol'>>;

  const handleDateUpdate = async (field: 'start' | 'end' | 'lmp', value: string) => {
    if (!cycleDetails?.id) return;
    // Prepare payload for updateDates API
    const payload: CycleDatesPayload = {};
    if (field === 'start') payload.cycle_start_date = value;
    if (field === 'end') payload.cycle_end_date = value;
    if (field === 'lmp') payload.lmp_date = value;
    try {
      await updateDates(cycleDetails.id.toString(), payload);
      // Optionally, refresh details from backend
      const updated = await getcycleList(cycleDetails.id.toString());
      setCycleDetails(updated);
      setEditedStartDate(updated.cycle_start_date || '');
      setEditedEndDate(updated.cycle_end_date || '');
      setEditedLmpDate(updated.lmp_date || '');
    } catch (err) {
      // Optionally, show error to user
      console.error('Failed to update date', err);
    }
  };
  const [showStepper, setShowStepper] = useState(false);
  const { setCycleId } = useCycle();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const cycleId = params.get('cycleId');
  const { id } = useParams<{ id: string }>();
  const [buttonStates, setButtonStates] = useState<ButtonState>();
  useEffect(() => {

    const genderValue: 'male' | 'female' | undefined =
      gender === 'MALE'
        ? 'male'
        : gender === 'FEMALE'
          ? 'female'
          : undefined;
    const allStates = getButtonStates('Clinical', 'Cycle Overview', 'Cycle Overview', genderValue);
    setButtonStates(allStates);

  }, [gender]);

  const [medicationProtocol, setMedicationProtocol] = useState<{ id: number; name: string }[]>([]);
  const [editingMedicationProtocol, setEditingMedicationProtocol] = useState(false);
  const [selectedMedicationProtocol, setSelectedMedicationProtocol] = useState<number | undefined>(undefined);
  
  const { canAdd, canEdit } = getButtonStates('Clinical', 'Cycle Overview', 'Cycle Overview', 'female');
    
  const [showPrint, setShowPrint] = useState<boolean>(false);

  useEffect(() => {
    getmedicationprotocol(ClinicId)
      .then((data: MedicationProtocolResponse) => {
        if (data && data.objects) {
          const protocols = data.objects.map((obj) => ({
            id: obj.id,
            name: obj.name,
          }));
          setMedicationProtocol(protocols);
        }
      })
      .catch((error) => {
        console.error('Error fetching medication protocols:', error);
      });
  }, [ClinicId]);

  // Set selectedMedicationProtocol when cycleDetails changes
  useEffect(() => {
    if (cycleDetails?.medication_protocol) {
      setSelectedMedicationProtocol(cycleDetails.medication_protocol);
    }
  }, [cycleDetails]);
  useEffect(() => {
    if (cycleId || cycle) {
      getcycleList(cycleId ? cycleId : cycle)
        .then((data) => {
          setCycleDetails(data);
          setEditedStartDate(data?.cycle_start_date || '');
          setEditedEndDate(data?.cycle_end_date || '');
          setEditedLmpDate(data?.lmp_date || '');

          const rawSteps = data?.procedure_overview_links;
          if (Array.isArray(rawSteps)) {
            let newSteps = rawSteps.map((stepObj: { action: string; is_completed: boolean }) => ({
              step: stepObj.action === 'IUI' ? stepObj.action : convertUnderscoreToTitle(stepObj.action),
              status: stepObj.is_completed,
            }));
            if (data.is_surrogate === true) {
              newSteps = newSteps.filter(s => s.step !== 'Embryo Transfer Outcome' && s.step !== 'Embryo Transfer');
            }
            setSteps(newSteps);
            setShowStepper(newSteps.length > 0);
          }
        })
        .catch((error) => {
          console.error('Error fetching cycle details:', error);
        });
    }
  }, [cycle, cycleId]);


  const AnimatedStepper = ({ activeStep, setActiveStepCallback }: { activeStep: number; setActiveStepCallback: (step: number) => void }) => {

    const handleStepClick = (index: number) => {
      setActiveStepCallback(index);
    };
    console.log('Active Step:', stepsArray);
    return (
      <div style={{ width: '100%' }}>
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          connector={<CustomConnector />}
          sx={{ padding: '0 2rem' }}
        >
          {stepsArray.map((stepData, index) => (
            <Step key={stepData.step} completed={stepData.status}>
              <StepLabel StepIconComponent={CustomStepIcon} onClick={() => handleStepClick(index)}>
                <span className={`${styles.stepstext} ${activeStep === index ? styles.activeStepText : ''}`}>{t(stepData.step)}</span>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
    );
  };



  const setActiveStepCallback = (step: number) => {
    setActiveStep(step);
  };

  const getStepLabel = (step: number) => {
    return stepsArray[step]?.step || t('UnknownStep');
  };

  const navigate = useNavigate();
  const { patientId } = usePatientContext();
  const [cycleOverviewReportData, setCycleOverviewReportData] = useState<CycleDetailsType | null>(null);

  useEffect(() => {
    if (patientId) {
      getcycleOverviewReport(String(patientId),  cycle)
        .then((data) => {
          setCycleOverviewReportData(data);
        })
        .catch((error) => {
          console.error('Error fetching cycle overview report:', error);
          setCycleOverviewReportData(null);
        });
    }
  }, [patientId, cycle]);
  const handleViewClick = () => {
    if (getStepLabel(activeStep) === 'Stimulation') {
      navigate(`/clinical/patients/stimulation/${id}?selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Oocyte Retrieval') {
      navigate(`/clinical/patients/surgery/${id}?surgeryType=OocyteRetrieval&selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'IUI') {
      navigate(`/lab/andrology/IUI/${patientId}`);
    }

    if (getStepLabel(activeStep) === 'Semen Processing') {
      navigate(`/lab/andrology/semen-processing/${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Fertilization') {
      navigate(`/lab/embryology/embryo-culture/${id}?selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Embryo Culture') {
      navigate(`/lab/embryology/embryo-culture/${id}?selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Outcome') {
      navigate(`/clinical/patients/outcome/${id}`);
    }
    if (getStepLabel(activeStep) === 'Embryo Transfer Outcome') {
      navigate(`/clinical/patients/surgery/${id}?surgeryType=EmbryoTransfer&selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Embryo Transfer') {
      navigate(`/clinical/patients/surgery/${id}?surgeryType=EmbryoTransfer&selectedPatientId=${patientId}`);
    }
    if (getStepLabel(activeStep) === 'Semen Freezing') {
      navigate(`/lab/andrology/semen-freezing/${patientId}`);
    }
  };


  // Get array of service names
  const serviceNames = cycleDetails?.services?.map(s => s.name) || [];

  // Logic to determine if Oocyte Retrieval should be shown
  const shouldShowOocyteRetrieval = !(
    serviceNames.includes('FET (Thaw Embryos)') &&
    !serviceNames.includes('Thaw of oocytes')
  );
  const handleClose = () => {
    setShowPrint(false);
  };
 
  return (
    <><div>
      <Grid2 container spacing={2} justifyContent={'space-between'}>
        <Grid2 size={5} display="flex" justifyContent="start">
          <IconButton onClick={() => {
            handleCycleListClick();
            setCycleId(null);
          }}>
            <img src={HPIBack} alt="Back" style={{ width: '2.5rem', height: '2.5rem' }} />
          </IconButton>
        </Grid2>
        <Tooltip title="Print Report">
          <IconButton onClick={() => {
            setShowPrint(true);
          }} sx={{ marginTop: '8px' }}>
            <PrinterIcon />
          </IconButton>
        </Tooltip>
      </Grid2>

    </div>
    <div>
      <ReusableReportComponent
        open={showPrint}
        onClose={handleClose}
        reportTitle={tGeneral('CycleOverview_Report')}
      >
        <CycleOverviewReport cycleOverviewReportData={cycleOverviewReportData} />
      </ReusableReportComponent>
    </div>
    <div style={{ width: '100%', padding: '2rem' }}>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} sm={2} md={2} className={styles.CycleDetails}>
          {t('CycleDetails')}
        </Grid>

        <Grid item xs={12} sm={10} md={10}>

          <Grid container spacing={3} alignItems="center">

            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleNumber')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.cycle_number || '-'}</p>
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleName')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.cycle_name_label || '-'}</p>
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{convertUnderscoreToTitle(t('Indication'))}</strong>
                <p className={styles.stepstext} style={{ wordWrap: 'break-word' }}>
                  {cycleDetails?.procedure?.indication_label || '-'}
                </p>
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('OocyteSource')}</strong>
                {cycleDetails?.procedure?.donor_info?.oocyte_source?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <AvatarGroup max={4}>
                      {cycleDetails.procedure.donor_info.oocyte_source.map(
                        (source: { first_name: string; last_name: string; medical_record_no: string; photo: string; }, index: number) => (
                          <Tooltip
                            key={index}
                            title={`${source.first_name || ''} ${source.last_name || 'Self'}${!source.last_name ? '' : ` , MRN : ${source.medical_record_no || '-'}`}`}
                            arrow
                          >
                            <Avatar alt={`${source.first_name} ${source.last_name}`} src={source.photo || undefined}>
                              {!source.photo &&
                                  `${source.first_name?.charAt(0) || ''}${source.last_name?.charAt(0) || ''}`}
                            </Avatar>
                          </Tooltip>
                        )
                      )}
                    </AvatarGroup>
                  </div>
                ) : (
                  <p className={styles.stepstext}>-</p>
                )}
              </div>
            </Grid>



            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('SpermSource')}</strong>
                {cycleDetails?.procedure?.donor_info?.sperm_source?.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    <AvatarGroup max={4}>
                      {cycleDetails.procedure.donor_info.sperm_source.map(
                        (source: { first_name: string; last_name: string; medical_record_no: string; photo: string; }, index: number) => (
                          <Tooltip
                            key={index}
                            title={`${source.first_name || ''} ${source.last_name || 'Partner'}${!source.last_name ? '' : ` , MRN : ${source.medical_record_no || '-'}`}`}
                            arrow
                          >
                            <Avatar alt={`${source.first_name} ${source.last_name}`} src={source.photo || undefined}>
                              {!source.photo &&
                                  `${source.first_name?.charAt(0) || ''}${source.last_name?.charAt(0) || ''}`}
                            </Avatar>
                          </Tooltip>
                        )
                      )}
                    </AvatarGroup>
                  </div>
                ) : (
                  <p className={styles.stepstext}>-</p>
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('Recipient')}</strong>
                {cycleDetails?.procedure?.donor_info?.recipient ? (
                  <Tooltip
                    title={`${cycleDetails.procedure.donor_info.recipient.first_name || ''} ${cycleDetails.procedure.donor_info.recipient.last_name || 'Self'}${!cycleDetails.procedure.donor_info.recipient.last_name ? '' : ` , MRN : ${cycleDetails.procedure.donor_info.recipient.medical_record_no || '-'}`}`}
                    arrow
                  >
                    <Avatar
                      alt={`${cycleDetails.procedure.donor_info.recipient.first_name} ${cycleDetails.procedure.donor_info.recipient.last_name}`}
                      src={cycleDetails.procedure.donor_info.recipient.photo || undefined}
                    >
                      {!cycleDetails.procedure.donor_info.recipient.photo &&
                          `${cycleDetails.procedure.donor_info.recipient.first_name?.charAt(0) || ''}${cycleDetails.procedure.donor_info.recipient.last_name?.charAt(0) || ''}`}
                    </Avatar>
                  </Tooltip>
                ) : (
                  <p className={styles.stepstext}>-</p>
                )}
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('MedicationProtocol')}</strong>
                {(cycleDetails?.cycle_name === 'Donor cycle' || cycleDetails?.cycle_name === 'Surrogacy Cycle') ? (
                  editingMedicationProtocol ? (
                    <select
                      value={selectedMedicationProtocol ?? ''}
                      onChange={e => setSelectedMedicationProtocol(Number(e.target.value))}
                      onBlur={async () => {
                        setEditingMedicationProtocol(false);
                        if (selectedMedicationProtocol !== cycleDetails?.medication_protocol && selectedMedicationProtocol) {
                          try {
                            await updateDates(cycleDetails.id.toString(), { medication_protocol: selectedMedicationProtocol });
                            const updated = await getcycleList(cycleDetails.id.toString());
                            setCycleDetails(updated);
                          } catch (err) {
                            console.error('Failed to update medication protocol', err);
                          }
                        }
                      }}
                      style={{ cursor: 'pointer', border: 'none' }}
                      autoFocus
                    >
                      <option value="">{t('SelectProtocol')}</option>
                      {medicationProtocol.map(protocol => (
                        <option key={protocol.id} value={protocol.id}>{protocol.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <p className={styles.stepstext} style={{ margin: 0 }}>
                        {cycleDetails?.medication_protocol_name_label || '-'}
                      </p>
                      {(cycleDetails?.is_editable && cycleDetails?.status === 'ACTIVE' && !cycleDetails?.medication_protocol_name && (buttonStates?.canAdd || buttonStates?.canEdit)) && (
                        <EditIcon sx={{ fontSize: 'large', cursor: 'pointer', color: '#3A80E2', ml: 1 }} onClick={() => setEditingMedicationProtocol(true)} />
                      )}
                    </div>
                  )
                ) : (
                  <p className={styles.stepstext}>{cycleDetails?.medication_protocol_name_label || '-'}</p>
                )}
              </div>
            </Grid>




            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleCreator')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.cycle_creator_name || '-'}</p>
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleCoordinator')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.coordinator_name || '-'}</p>
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleProvider')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.provider_name || '-'}</p>
              </div>
            </Grid>


            {/* Editable Dates Section */}
            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleStartDate')}</strong>
                {editingDateField === 'start' && (canAdd || canEdit) ? ( 
                  <input
                    type="date"
                    value={editedStartDate || ''}
                    onChange={e => setEditedStartDate(e.target.value)}
                    style={{ cursor: 'pointer', border: 'none' }}
                    autoFocus
                    onBlur={async () => {
                      setEditingDateField(null);
                      if (editedStartDate && editedStartDate !== cycleDetails?.cycle_start_date) {
                        await handleDateUpdate('start', editedStartDate);
                      }
                    }}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center',}}>
                    <p className={styles.stepstext} style={{ margin: 0 }}>{formatDate(cycleDetails?.cycle_start_date) || '-'}</p>
                    {(cycleDetails?.is_editable && cycleDetails?.status === 'ACTIVE' && (buttonStates?.canAdd || buttonStates?.canEdit)) && (
                      <EditIcon sx={{ fontSize: 'large', cursor: 'pointer', color: '#3A80E2', ml: 1 }} onClick={() => setEditingDateField('start')} />
                    )}
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('CycleEndDate')}</strong>
                {editingDateField === 'end'  && (canAdd || canEdit) ? (
                  <input
                    type="date"
                    value={editedEndDate || ''}
                    onChange={e => setEditedEndDate(e.target.value)}
                    style={{ cursor: 'pointer', border: 'none' }}
                    autoFocus
                    onBlur={async () => {
                      setEditingDateField(null);
                      if (editedEndDate && editedEndDate !== cycleDetails?.cycle_end_date) {
                        await handleDateUpdate('end', editedEndDate);
                      }
                    }}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center',  }}>
                    <p className={styles.stepstext} style={{ margin: 0 }}>{formatDate(cycleDetails?.cycle_end_date) || '-'}</p>
                    {(cycleDetails?.is_editable && cycleDetails?.status === 'ACTIVE' && (buttonStates?.canAdd || buttonStates?.canEdit)) && (
                      <EditIcon sx={{ fontSize: 'large', cursor: 'pointer', color: '#3A80E2', ml: 1 }} onClick={() => setEditingDateField('end')} />
                    )}
                  </div>
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('LMPDate')}</strong>
                {editingDateField === 'lmp' && (canAdd || canEdit) && !isLmpDisabled ? (
                  <input
                    type="date"
                    value={editedLmpDate || ''}
                    onChange={e => setEditedLmpDate(e.target.value)}
                    style={{ cursor: 'pointer', border: 'none' }}
                    autoFocus
                    onBlur={async () => {
                      setEditingDateField(null);
                      if (editedLmpDate && editedLmpDate !== cycleDetails?.lmp_date) {
                        await handleDateUpdate('lmp', editedLmpDate);
                      }
                    }}
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', }}>
                    <p className={styles.stepstext} style={{ margin: 0 }}>{formatDate(cycleDetails?.lmp_date) || '-'}</p>
                    {(cycleDetails?.is_editable && cycleDetails?.status === 'ACTIVE' && (buttonStates?.canAdd || buttonStates?.canEdit)) && (
                      <EditIcon sx={{ fontSize: 'large', cursor: 'pointer', color: '#3A80E2', ml: 1 }} onClick={() => setEditingDateField('lmp')} />
                    )}
                  </div>
                )}
              </div>
            </Grid>


            <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.headerCycleDetails}>{t('Remark')}</strong>
                <p className={styles.stepstext}>{cycleDetails?.remarks || '-'}</p>
              </div>
            </Grid>


            {/* <Grid item xs={12} sm={6} md={3}>
              <div className={styles.textField}>
                <strong className={styles.cycleList} onClick={() => {
                  handleCycleListClick();
                  setCycleId(null);
                } }
                >
                    Cycle List
                </strong>
              </div>
            </Grid> */}
          </Grid>

        </Grid>
      </Grid>

      {showStepper && (
        <div className={`${styles.boxstepper} mt-4`}>
          <Grid container justifyContent="center" spacing={3}>
            <Grid item xs={12} sm={12} md={12}>
              <AnimatedStepper activeStep={activeStep} setActiveStepCallback={setActiveStepCallback} />
            </Grid>
          </Grid>
        </div>
      )}

      <div className="mt-4">
        <span className={styles.CycleDetailsStep}>
          {t(getStepLabel(activeStep))} <span className={styles.CycleDetailsStepView} onClick={handleViewClick}>{t('View')}</span>
        </span>
      </div>
      <div className="mt-4">
        {{
          'Stimulation': cycleDetails?.stimulation_chart ? <Stimulation stimulationChart={cycleDetails.stimulation_chart} /> : <Stimulation />,
          'Oocyte Retrieval': shouldShowOocyteRetrieval ? <OocyteRetrieval cycle={cycle} /> : null,
          'Semen Processing': <SemenProcessing cycle={cycle} />,
          'Semen Freezing': <SemenFreezingReport cycle={cycle} />,
          'IUI': <IUIDetails cycle={cycle} />,
          'Fertilization': <Fertilization cycle={cycle} />,
          'Embryo Culture': <EmbryoCulture cycle={cycle} />,
          'Embryo Transfer Outcome': <EmbryoTransferOutcome cycle={cycle} cycle_name={cycleDetails ? cycleDetails?.cycle_name : ''} />,
          'Embryo Transfer': <EmbryoTransferOutcome cycle={cycle} cycle_name={cycleDetails ? cycleDetails?.cycle_name : ''} />,
          'Outcome': <EmbryoTransferOutcome cycle={cycle} cycle_name={cycleDetails ? cycleDetails?.cycle_name : ''} />
        }[getStepLabel(activeStep)] || null}
      </div>
    </div>
    </>
  );
};

export default CycleDetails;
